// Message-thread layout for a single NodeExecution's expanded detail.
// Replaces the old three-`<pre>JSON.stringify(…)</pre>` blocks that DEV-16124
// shipped. Mirrors triage's `TaskNode` quote-card aesthetic — markdown
// bodies, role-aware coloring, per-bubble timestamp + role label.
// (DEV-16173.)
//
// Two render paths, picked by what data is available on the execution:
//
//   1. `trace.messages` is an array → render one bubble per message in
//      order. This is the live LLM-agent path: the runner captures the
//      Mastra response's full conversation and persists it.
//
//   2. Fallback → synthesize bubbles from the legacy fields:
//      `input` becomes a "user" bubble, `output` becomes an "assistant"
//      bubble, `error` (when present) becomes a danger bubble. Same
//      visual idiom, just no per-turn fidelity.
//
// DEV-16201: pair every tool-call breadcrumb with its tool-result by
// `toolCallId` so the UI shows a single inline card with tool name +
// collapsible input/output (mirrors platform-app's investigation
// `ToolCallBlock`). The two messages stay separate at the storage
// layer; pairing is purely a render-time pass.
//
// DEV-16201 (follow-up): the fallback path used to synthesize fake
// `user` / `assistant` bubbles for non-LLM nodes (code-exec, threshold-
// check, template-render, passthrough …). That labeling was misleading
// — a code execution doesn't have chat turns, just an in/out shape.
// Synthesized rows now render as plainly-labeled INPUT / OUTPUT cards
// with a neutral border, separate from the real-message bubble path,
// so a debugger reading the run-detail panel can immediately tell the
// difference between an actual LLM message and a JSON IO pair.
import type { NodeExecution, NodeMessage } from '@cortex/types';
import { useState, type CSSProperties, type ReactNode } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

// Markdown styles (`.md-prose ul/ol/li/code/h*` …) for the message bodies
// rendered below. Without this import the only places loading the
// stylesheet are `ExtractionSchemas` and the `knowledge-graphs` route,
// so the run-trace surface gets browser-default list rendering — and
// the unstyled `<ul>` overflows past the message bubble's left border
// onto the canvas. (DEV-16213.)
import '../common/markdown-prose.css';

const thread: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--sp-3)',
  padding: '0 var(--sp-3) var(--sp-3) var(--sp-3)',
};

const bubble: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--sp-1)',
  paddingLeft: 'var(--sp-3)',
  paddingTop: 'var(--sp-2)',
  paddingBottom: 'var(--sp-2)',
  paddingRight: 'var(--sp-2)',
  background: 'var(--surface-2)',
  borderLeft: '2px solid var(--border-strong)',
  borderTopRightRadius: 'var(--radius-sm)',
  borderBottomRightRadius: 'var(--radius-sm)',
  // Flex children default to `min-width: auto`, which keeps the bubble
  // wider than its container if any child has long unbreakable content.
  // Force shrink so the inner markdown box can actually wrap.
  minWidth: 0,
};

const bubbleHeader: CSSProperties = {
  display: 'flex',
  alignItems: 'baseline',
  justifyContent: 'space-between',
  gap: 'var(--sp-2)',
  fontSize: 'var(--fs-xs)',
};

const roleLabel: CSSProperties = {
  fontFamily: 'var(--font-mono, monospace)',
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
  fontWeight: 600,
};

// The DEV-16196 node-name eyebrow: rendered at the front of every
// bubble's header in primary text color so it reads as the bubble's
// owner. The role + agent suffixes deliberately drop in opacity so
// the node identity wins the visual hierarchy.
const nodeLabelStyle: CSSProperties = {
  color: 'var(--text-primary)',
  fontWeight: 700,
};

const timestamp: CSSProperties = {
  color: 'var(--text-secondary)',
};

const markdownBody: CSSProperties = {
  fontSize: 'var(--fs-sm)',
  lineHeight: 1.5,
  color: 'var(--text-primary)',
  // Long no-space tokens (JSON dumps, URLs, IDs) used to bleed past the
  // bubble's right edge — `overflow: hidden` was clipping them instead
  // of letting them wrap. `overflow-wrap: anywhere` permits a break at
  // any character when needed; normal prose still breaks at word
  // boundaries first.
  overflowWrap: 'anywhere',
};

const toolCallCard: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
  marginTop: 'var(--sp-2)',
  padding: 'var(--sp-2)',
  background: 'var(--surface)',
  border: '1px solid var(--border-subtle)',
  borderLeft: '2px solid var(--terminal-amber)',
  borderRadius: 'var(--radius-xs)',
  fontSize: 'var(--fs-xs)',
};

const toolCallCardError: CSSProperties = {
  ...toolCallCard,
  borderLeftColor: 'var(--status-danger)',
  background: 'color-mix(in srgb, var(--status-danger) 6%, var(--surface))',
};

const toolCallHeader: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 'var(--sp-2)',
};

const toolCallControls: CSSProperties = {
  display: 'flex',
  gap: 'var(--sp-1)',
  flexWrap: 'wrap',
};

const toolCallToggle: CSSProperties = {
  fontSize: 11,
  padding: '2px 6px',
};

const codeBlock: CSSProperties = {
  background: 'var(--surface)',
  padding: 'var(--sp-2)',
  borderRadius: 'var(--radius-xs)',
  overflow: 'auto',
  fontSize: 12,
  lineHeight: 1.45,
  margin: 0,
  maxHeight: 240,
  // Keep newlines but allow soft-wrap on long lines so a wide JSON
  // payload doesn't force a horizontal scroll inside an already-narrow
  // side panel.
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word',
};

const codeBlockError: CSSProperties = {
  ...codeBlock,
  background: 'color-mix(in srgb, var(--status-danger) 8%, var(--surface))',
  border: '1px solid color-mix(in srgb, var(--status-danger) 30%, transparent)',
};

const errorBubble: CSSProperties = {
  ...bubble,
  borderLeftColor: 'var(--status-danger)',
  background: 'color-mix(in srgb, var(--status-danger) 8%, var(--surface-2))',
};

// Synthesized IO row — same layout as `bubble` but a deliberately neutral
// border + slightly muted background so it doesn't visually compete with
// real LLM-message bubbles. The eyebrow ("INPUT" / "OUTPUT") carries the
// semantic difference. (DEV-16201 follow-up.)
const ioBubble: CSSProperties = {
  ...bubble,
  borderLeftColor: 'var(--border-subtle)',
  background: 'var(--surface)',
};

const remarkPlugins = [remarkGfm];

// ReactMarkdown's default <pre>/<code> render with `white-space: pre`,
// which lets a long JSON line bleed past the bubble. Override so fenced
// blocks soft-wrap (newlines still respected) inside narrow side panels.
const markdownComponents = {
  pre: (props: { children?: ReactNode }) => <pre style={codeBlock}>{props.children}</pre>,
  code: (props: { children?: ReactNode; className?: string }) =>
    // Distinguish inline `code` vs block code. ReactMarkdown sets a
    // `language-*` class only on block-level code (inside <pre>); inline
    // code has no className. The <pre> wrapper above handles the block
    // case, so inline just needs a wrap hint.
    props.className ? (
      <code className={props.className}>{props.children}</code>
    ) : (
      <code style={{ wordBreak: 'break-word' }}>{props.children}</code>
    ),
};

export function RunMessageThread({ execution }: { execution: NodeExecution }) {
  const messages = readMessages(execution);
  // Resolve the bubble owner once per thread. The DEV-16196 ticket
  // asks for every block — system, tool, assistant, user — to show
  // which node it came from. NodeExecution rows now carry `nodeKey`
  // from the API join; legacy rows that don't get the joined value
  // fall back to a short `nodeId` slice rather than blank.
  const nodeLabel = execution.nodeKey ?? `${execution.nodeId.slice(0, 8)}…`;
  if (messages.length > 0) {
    // Pair tool-call breadcrumbs with their matching tool-result so the UI
    // renders them as a single inline card (platform-app's `ToolCallBlock`
    // pattern: tool name + collapsible "Show input" / "Show output").
    // The standalone tool-result bubble that used to render below is
    // absorbed into its paired call, and an unpaired tool-result (no
    // matching call seen yet — rare, stream interleaving) still renders
    // as its own bubble so nothing is silently dropped. (DEV-16201.)
    const resultByCallId = indexToolResults(messages);
    return (
      <div style={thread}>
        {messages.map((m, i) => {
          if (m.role === 'tool' && m.toolCallId && resultByCallId.has(m.toolCallId)) {
            // Skip — already absorbed into its paired assistant card.
            return null;
          }
          return (
            <MessageBubble
              key={`${m.createdAt}-${i}`}
              message={m}
              nodeLabel={nodeLabel}
              pairedResult={pairedResultFor(m, resultByCallId)}
              isError={m.role === 'tool' && isErrorTool(m)}
            />
          );
        })}
        {execution.error ? <ErrorCard error={execution.error} /> : null}
      </div>
    );
  }

  // Fallback path: non-LLM node (code-exec, threshold-check, etc.) or a
  // legacy LLM run that pre-dates trace.messages capture. Render the
  // raw input + output as IOBubble cards — INPUT / OUTPUT labels with a
  // neutral border — so the UI is honest about the fact that these are
  // synthesized from `execution.input` / `execution.output` rather than
  // real chat turns. (DEV-16201 follow-up to Richard's Slack feedback.)
  const synthetic = synthesizeMessages(execution);
  return (
    <div style={thread}>
      {synthetic.map((row, i) => (
        <IOBubble key={i} row={row} nodeLabel={nodeLabel} />
      ))}
      {execution.error ? <ErrorCard error={execution.error} /> : null}
    </div>
  );
}

/**
 * Renders a synthesized IO row — input or output — for non-LLM nodes
 * (or legacy LLM runs). Visually a quiet sibling of `MessageBubble`:
 * same column layout, same markdown body, but with a literal
 * "INPUT" / "OUTPUT" eyebrow and a neutral border so it doesn't claim
 * to be an LLM chat turn. (DEV-16201 follow-up.)
 */
function IOBubble({ row, nodeLabel }: { row: ISynthesizedRow; nodeLabel: string }) {
  return (
    <div style={ioBubble}>
      <div style={bubbleHeader}>
        <span style={{ ...roleLabel, color: 'var(--text-secondary)' }}>
          <span style={nodeLabelStyle} title={`Produced by node ${nodeLabel}`}>
            {nodeLabel}
          </span>
          <span style={{ opacity: 0.7 }}> · {row.kind}</span>
        </span>
        <span style={timestamp}>{formatTime(row.createdAt)}</span>
      </div>
      <div style={markdownBody} className="md-prose">
        <ReactMarkdown remarkPlugins={remarkPlugins} components={markdownComponents}>
          {row.content}
        </ReactMarkdown>
      </div>
    </div>
  );
}

/**
 * Build a quick `toolCallId → tool-result message` lookup so the bubble
 * renderer can attach a tool-result body to the assistant card that
 * issued the matching call, instead of rendering them as two adjacent
 * bubbles.
 */
function indexToolResults(messages: NodeMessage[]): Map<string, NodeMessage> {
  const out = new Map<string, NodeMessage>();
  for (const m of messages) {
    if (m.role === 'tool' && m.toolCallId) out.set(m.toolCallId, m);
  }
  return out;
}

function pairedResultFor(
  message: NodeMessage,
  resultByCallId: Map<string, NodeMessage>,
): NodeMessage | undefined {
  if (message.role !== 'assistant' || !message.toolCalls?.length) return undefined;
  // Today we emit exactly one tool-call per assistant breadcrumb, so the
  // first entry is the one to pair. If Mastra ever batches calls into a
  // single chunk we'll handle the rest as additional cards on the same
  // bubble (already supported by ToolCallsSection).
  const id = message.toolCalls[0]?.id;
  return id ? resultByCallId.get(id) : undefined;
}

function MessageBubble({
  message,
  nodeLabel,
  pairedResult,
  isError,
}: {
  message: NodeMessage;
  nodeLabel: string;
  pairedResult?: NodeMessage;
  isError?: boolean;
}) {
  const showAsMarkdown = !!message.content && message.content.trim().length > 0;
  // Reasoning bubbles default to collapsed — they can run pages long on
  // extended-thinking models and the user normally cares about the final
  // answer + tool calls. A single click reveals the body. Click handler
  // toggles. (DEV-16201.)
  const [reasoningOpen, setReasoningOpen] = useState(false);
  const isReasoning = message.role === 'reasoning';
  const bodyHidden = isReasoning && !reasoningOpen;
  return (
    <div style={isError ? errorBubble : roleBubble(message.role)}>
      <div style={bubbleHeader}>
        <span style={{ ...roleLabel, color: roleColor(message.role) }}>
          {/* Node ownership prefix — guaranteed present on every bubble
              regardless of role, so a debugger scanning the thread can
              correlate each message back to the node that produced it.
              The role + optional agent name remain after for context.
              (DEV-16196.) */}
          <span style={nodeLabelStyle} title={`Produced by node ${nodeLabel}`}>
            {nodeLabel}
          </span>
          <span style={{ opacity: 0.7 }}> · {message.role}</span>
          {message.name ? <span style={{ opacity: 0.6 }}> · {message.name}</span> : null}
        </span>
        <span style={timestamp}>{formatTime(message.createdAt)}</span>
      </div>
      {isReasoning ? (
        <button
          type="button"
          className="btn btn--ghost btn--xs"
          onClick={() => setReasoningOpen((open) => !open)}
          style={{ alignSelf: 'flex-start', fontSize: 11, padding: '2px 6px' }}
          aria-expanded={reasoningOpen}
        >
          {reasoningOpen ? '▾ Hide reasoning' : '▸ Show reasoning'}
        </button>
      ) : null}
      {!bodyHidden && showAsMarkdown ? (
        <div style={markdownBody} className="md-prose">
          <ReactMarkdown remarkPlugins={remarkPlugins} components={markdownComponents}>
            {message.content}
          </ReactMarkdown>
        </div>
      ) : !isReasoning && !showAsMarkdown && !message.toolCalls?.length ? (
        <span className="t-xs t-secondary">(no body)</span>
      ) : null}
      {message.toolCalls?.length ? (
        <ToolCallsSection calls={message.toolCalls} pairedResult={pairedResult} />
      ) : null}
    </div>
  );
}

/**
 * One inline card per tool call. Renders like platform-app's
 * `ToolCallBlock`: tool name as the header, expandable "Show input" /
 * "Show output" toggles that reveal the JSON args + result body. When
 * the call hasn't received its result yet (still mid-loop), the output
 * row reads "running…". Errors get a red border. (DEV-16201.)
 */
function ToolCallsSection({
  calls,
  pairedResult,
}: {
  calls: NonNullable<NodeMessage['toolCalls']>;
  pairedResult?: NodeMessage;
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-1)' }}>
      {calls.map((c, i) => (
        <ToolCallBlock
          key={c.id || `call-${i}`}
          call={c}
          // Today there's at most one tool call per assistant breadcrumb,
          // so the paired result attaches to the first entry. Additional
          // calls in the same message render input-only — until/unless
          // Mastra batches them with matching results.
          result={i === 0 ? pairedResult : undefined}
        />
      ))}
    </div>
  );
}

function ToolCallBlock({
  call,
  result,
}: {
  call: NonNullable<NodeMessage['toolCalls']>[number];
  result?: NodeMessage;
}) {
  const [showInput, setShowInput] = useState(false);
  const [showOutput, setShowOutput] = useState(false);
  // `isError` is the `processStream`-injected extension carried on
  // tool-error chunks. The schema doesn't formalise it (would force a
  // round-trip through a NodeMessage type bump for every consumer) so
  // we read it via a typed cast — falsy by default.
  const isError = (result as NodeMessage & { isError?: boolean })?.isError === true;
  const hasArgs = call.args && Object.keys(call.args).length > 0;
  const hasResult = !!result;
  return (
    <div style={isError ? toolCallCardError : toolCallCard}>
      <div style={toolCallHeader}>
        <span
          style={{
            ...roleLabel,
            color: isError ? 'var(--status-danger)' : 'var(--terminal-amber)',
            fontSize: 11,
          }}
        >
          🔧 {call.name || 'unknown'}
        </span>
        {!hasResult ? (
          <span className="t-xs t-secondary" style={{ fontStyle: 'italic' }}>
            running…
          </span>
        ) : isError ? (
          <span className="t-xs" style={{ color: 'var(--status-danger)' }}>
            error
          </span>
        ) : (
          <span className="t-xs t-secondary">done</span>
        )}
      </div>
      <div style={toolCallControls}>
        <button
          type="button"
          className="btn btn--ghost btn--xs"
          onClick={() => setShowInput((open) => !open)}
          disabled={!hasArgs}
          style={toolCallToggle}
          aria-expanded={showInput}
        >
          {showInput ? '▾ Hide input' : '▸ Show input'}
        </button>
        <button
          type="button"
          className="btn btn--ghost btn--xs"
          onClick={() => setShowOutput((open) => !open)}
          disabled={!hasResult}
          style={toolCallToggle}
          aria-expanded={showOutput}
        >
          {showOutput ? '▾ Hide output' : '▸ Show output'}
        </button>
      </div>
      {showInput && hasArgs ? (
        <pre style={codeBlock}>{JSON.stringify(call.args, null, 2)}</pre>
      ) : null}
      {showOutput && hasResult ? (
        <pre style={isError ? codeBlockError : codeBlock}>{renderToolResultBody(result)}</pre>
      ) : null}
    </div>
  );
}

function renderToolResultBody(message: NodeMessage): string {
  const body = message.content;
  // tool-result chunks emit `content` as either a string (already-formatted
  // tool output) or a JSON-stringified object. Pretty-print the latter so
  // the modal box is readable.
  try {
    const parsed = JSON.parse(body) as unknown;
    return JSON.stringify(parsed, null, 2);
  } catch {
    return body;
  }
}

function ErrorCard({ error }: { error: NodeExecution['error'] }) {
  if (!error) return null;
  const body = typeof error === 'string' ? error : JSON.stringify(error, null, 2);
  return (
    <div style={errorBubble}>
      <div style={bubbleHeader}>
        <span style={{ ...roleLabel, color: 'var(--status-danger)' }}>error</span>
      </div>
      <pre style={codeBlock}>{body}</pre>
    </div>
  );
}

function readMessages(execution: NodeExecution): NodeMessage[] {
  const trace = execution.trace;
  if (!trace || typeof trace !== 'object') return [];
  const maybe = (trace as Record<string, unknown>).messages;
  if (!Array.isArray(maybe)) return [];
  const out: NodeMessage[] = [];
  for (const m of maybe) {
    if (!m || typeof m !== 'object') continue;
    const raw = m as Record<string, unknown>;
    if (typeof raw.role !== 'string') continue;
    if (!['system', 'user', 'assistant', 'tool', 'reasoning'].includes(raw.role)) continue;
    out.push({
      id: typeof raw.id === 'string' ? raw.id : undefined,
      role: raw.role as NodeMessage['role'],
      content: typeof raw.content === 'string' ? raw.content : '',
      name: typeof raw.name === 'string' ? raw.name : undefined,
      toolCallId: typeof raw.toolCallId === 'string' ? raw.toolCallId : undefined,
      toolCalls: Array.isArray(raw.toolCalls)
        ? (raw.toolCalls as NodeMessage['toolCalls'])
        : undefined,
      createdAt: typeof raw.createdAt === 'string' ? raw.createdAt : new Date().toISOString(),
      // `isError` rides as a typed extension — round-tripped here so the
      // ToolCallBlock can highlight error results.
      ...(raw.isError === true ? { isError: true } : {}),
    } as NodeMessage);
  }
  return out;
}

interface ISynthesizedRow {
  kind: 'input' | 'output';
  content: string;
  createdAt: string;
}

/**
 * For non-LLM nodes (or older runs that pre-date the trace capture),
 * derive an INPUT / OUTPUT pair from `execution.input` / `execution.output`
 * so the visual idiom stays consistent across node types. Not lossless —
 * but honest: the rows are labeled for what they are (raw IO) instead of
 * borrowing the LLM chat-turn vocabulary (`user` / `assistant`), which
 * was misleading on code-exec / threshold-check / template-render nodes
 * that never produced chat turns. (DEV-16201 follow-up.)
 */
function synthesizeMessages(execution: NodeExecution): ISynthesizedRow[] {
  const out: ISynthesizedRow[] = [];
  if (execution.input) {
    out.push({
      kind: 'input',
      content: '```json\n' + JSON.stringify(execution.input, null, 2) + '\n```',
      createdAt:
        execution.startedAt instanceof Date
          ? execution.startedAt.toISOString()
          : ((execution.startedAt as unknown as string) ?? new Date().toISOString()),
    });
  }
  if (execution.output) {
    out.push({
      kind: 'output',
      content: renderOutput(execution.output),
      createdAt:
        execution.finishedAt instanceof Date
          ? execution.finishedAt.toISOString()
          : ((execution.finishedAt as unknown as string) ?? new Date().toISOString()),
    });
  }
  return out;
}

function renderOutput(output: unknown): string {
  if (typeof output === 'object' && output !== null) {
    const o = output as Record<string, unknown>;
    // Common shape: `{ text: '...' }` — render the text directly so it
    // shows up as proper markdown instead of a JSON dump.
    if (typeof o.text === 'string' && o.text.trim().length > 0) return o.text;
  }
  return '```json\n' + JSON.stringify(output, null, 2) + '\n```';
}

function roleBubble(role: NodeMessage['role']): CSSProperties {
  return { ...bubble, borderLeftColor: roleColor(role) };
}

function roleColor(role: NodeMessage['role']): string {
  switch (role) {
    case 'system':
      return 'var(--text-muted, var(--text-secondary))';
    case 'user':
      return 'var(--border-strong)';
    case 'assistant':
      return 'var(--horizon-glow)';
    case 'tool':
      return 'var(--terminal-amber)';
    case 'reasoning':
      // Muted lavender so the model's hidden thinking reads as distinct
      // from the assistant's user-facing answer, without competing with
      // the horizon-glow / amber accents that mark important state.
      return 'var(--text-muted, var(--text-secondary))';
    default:
      return 'var(--border-subtle)';
  }
}

function formatTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleTimeString(undefined, {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

function isErrorTool(message: NodeMessage): boolean {
  return /error|failed/i.test(message.content);
}
